import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, Badge, Button, Divider, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem } from '@mui/material';
import { Notifications as NotificationsIcon, Send, MarkEmailRead } from '@mui/icons-material';
import { notificationsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const Notifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [formData, setFormData] = useState({ title: '', message: '', type: 'announcement', priority: 'medium', target_type: 'all' });

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    const [res, countRes] = await Promise.all([
      notificationsAPI.getAll({ limit: 50 }),
      notificationsAPI.getUnreadCount(),
    ]);
    if (res.data.success) setNotifications(res.data.data.notifications);
    if (countRes.data.success) setUnreadCount(countRes.data.data.unread_count);
  };

  const handleMarkRead = async (id) => {
    await notificationsAPI.markRead(id);
    loadNotifications();
  };

  const handleMarkAllRead = async () => {
    await notificationsAPI.markAllRead();
    loadNotifications();
  };

  const handleSend = async () => {
    await notificationsAPI.send(formData);
    setShowSendDialog(false);
    setFormData({ title: '', message: '', type: 'announcement', priority: 'medium', target_type: 'all' });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const formatTime = (notif) => {
    // Try both created_at and createdAt formats from Sequelize
    const dateValue = notif?.created_at || notif?.createdAt;
    if (!dateValue) return 'Unknown';
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return 'Unknown';
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return d.toLocaleDateString();
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', p: 0 }}>
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Notifications</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<MarkEmailRead />} onClick={handleMarkAllRead}>Mark All Read</Button>
            {(user?.role === 'admin' || user?.role === 'hr') && (
              <Button variant="contained" startIcon={<Send />} onClick={() => setShowSendDialog(true)}>Send Notification</Button>
            )}
          </Box>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <List>
              {notifications.map((notif, index) => (
                <React.Fragment key={notif.id}>
                  <ListItem alignItems="flex-start" sx={{ bgcolor: notif.is_read ? 'transparent' : '#000724fb', cursor: 'pointer' }} onClick={() => !notif.is_read && handleMarkRead(notif.id)}>
                    <ListItemAvatar>
                      <Badge badgeContent={notif.is_read ? 0 : 1} color="error">
                        <Avatar sx={{ bgcolor: notif.type === 'alert' ? '#df0505' : '#ddc910' }}><NotificationsIcon /></Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Typography variant="subtitle1" sx={{ fontWeight: notif.is_read ? 400 : 600 }}>{notif.title}</Typography><Chip size="small" label={notif.priority} color={getPriorityColor(notif.priority)} /></Box>}
                      secondary={<><Typography variant="body2" sx={{ color: '#1a8b03' }}>{notif.message}</Typography><Typography variant="caption" sx={{ color: '#179903' }}>{formatTime(notif)}</Typography></>}
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {notifications.length === 0 && (
                <ListItem><ListItemText secondary="No notifications" sx={{ textAlign: 'center' }} /></ListItem>
              )}
            </List>
          </CardContent>
        </Card>

        <Dialog open={showSendDialog} onClose={() => setShowSendDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Notification</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} sx={{ mt: 2, mb: 2 }} />
            <TextField fullWidth label="Message" multiline rows={3} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} sx={{ mb: 2 }} />
            <TextField select fullWidth label="Type" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} sx={{ mb: 2 }}>
              <MenuItem value="announcement">Announcement</MenuItem>
              <MenuItem value="alert">Alert</MenuItem>
              <MenuItem value="reminder">Reminder</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </TextField>
            <TextField select fullWidth label="Priority" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions><Button onClick={() => setShowSendDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleSend}>Send</Button></DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Notifications;
